<script setup lang="ts">
  import { Calendar, CalendarSize, Floating, FloatingPlacement, DateISO, CalendarDateRange } from '@travelata-libs/ui-kit';
  import { onBeforeUnmount, watch } from 'vue';

  import DateFromFlexChips from './DateFromFlexChips.vue';

  interface DateFromDropdownProps {
    anchorIso: string;
    flexibleForCalendar?: number | null;
    flexible: number;
    minDate: string;
    maxDate: string;
    visible: boolean;
  }

  const props = withDefaults(defineProps<DateFromDropdownProps>(), {
    flexibleForCalendar: null,
  });

  interface DateFromDropdownEmits {
    (e: 'date-click', iso: string): void;
    (e: 'select-flexible', n: number): void;
    (e: 'close'): void;
  }

  const emit = defineEmits<DateFromDropdownEmits>();

  const onDateClick = (payload: DateISO | CalendarDateRange<DateISO> | null): void => {
    if (typeof payload === 'string') {
      emit('date-click', payload);
    } else if(payload?.start) {
      emit('date-click', payload.start);
    }
  };

  const onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      emit('close');
    }
  };

  const onSelectFlexible = (n: number) => {
    emit('select-flexible', n)
  }

  const onHide = (): void => {
    if (props.visible) {
      emit('close');
    }
  };

  watch(
    () => props.visible,
    (visible) => {
      if (visible) {
        document.addEventListener('keydown', onKeydown);
      } else {
        document.removeEventListener('keydown', onKeydown);
      }
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', onKeydown);
  });
</script>

<template>
  <Floating
    :visible="visible"
    :placement="FloatingPlacement.Bottom"
    :teleported="false"
    @hide="onHide"
  >
    <template #reference>
      <slot name="reference" />
    </template>

    <template #floating>
      <div class="date-from-dropdown" data-testid="date-from-dropdown">
        <Calendar
          data-testid="date-from-calendar"
          :model-value="anchorIso"
          double
          :size="CalendarSize.M"
          :range="false"
          :flexible-days="flexibleForCalendar"
          :with-footer="false"
          :min-date="minDate"
          :max-date="maxDate"
          @date-click="onDateClick"
        />

        <DateFromFlexChips :value="flexible" @select="onSelectFlexible" />
      </div>
    </template>
  </Floating>
</template>

<style scoped lang="scss">
  .date-from-dropdown {
    background: var(--color-bg-primary);
    border-radius: var(--rounding-s);
    box-shadow: var(--shadow-m);
    display: flex;
    flex-direction: column;
    padding: var(--spacing-l);
    width: max-content;
  }
</style>
